import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import subscriptionService from '../../utils/services/subscriptionService';
import devToolsService from '../../utils/services/devToolsService';
import Toast from '../Toast/Toast';
import SubscriptionPlansModal from '../SubscriptionPlansModal/SubscriptionPlansModal';
import ManageExistingSportSubscription from './ManageExistingSportSubscription';
import aflLogo from '../../assets/afl_logo.png';
import nbaLogo from '../../assets/nba_logo.png';

function ManageSubscriptions() {
    const [subscriptions, setSubscriptions] = useState([]);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [isPlansModalOpen, setIsPlansModalOpen] = useState(false);
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [successBanner, setSuccessBanner] = useState(null);
    const [isPaymentMethodLoading, setIsPaymentMethodLoading] = useState(false);
    const [isBillingHistoryLoading, setIsBillingHistoryLoading] = useState(false);
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Dev tools state
    const isDevMode = process.env.REACT_APP_PASSWORD_PROTECTED === 'true';
    const [devToolsExpanded, setDevToolsExpanded] = useState({});
    const [advanceDays, setAdvanceDays] = useState({});
    const [isAdvancingClock, setIsAdvancingClock] = useState({});
    const [testClockInfo, setTestClockInfo] = useState({});

    const showToast = useCallback((message, type = 'error') => {
        setToast({ message, type });
    }, []);

    const fetchSubscriptions = useCallback(async () => {
        setIsLoading(true);
        
        const result = await subscriptionService.getUserSubscriptions();

        if (result.success) {
            setSubscriptions(result.data);
        } else {
            // Handle token errors - logout and redirect
            if (result.requiresLogout) {
                showToast(result.message, 'error');
                
                // Logout and redirect after a short delay
                setTimeout(() => {
                    logout();
                    navigate('/');
                }, 2000);
            } else {
                showToast(result.message, 'error');
            }
        }
        
        setIsLoading(false);
    }, [logout, navigate, showToast]);

    const fetchAvailablePlans = useCallback(async () => {
        const result = await subscriptionService.getAllPlans();
        if (result.success) {
            setAvailablePlans(result.data);
        }
    }, []);

    const closeToast = () => {
        setToast(null);
    };

    useEffect(() => {
        fetchSubscriptions();
        fetchAvailablePlans();
    }, [fetchSubscriptions, fetchAvailablePlans]);

    // Separate effect to check for successful Stripe redirect
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
            showToast('🎉 Subscription successful! Welcome aboard!', 'success');
            
            // Remove session_id from URL
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('session_id');
            setSearchParams(newSearchParams, { replace: true });
            
            // Refresh subscriptions to show the new one
            setTimeout(() => {
                fetchSubscriptions();
            }, 1000);
        }
    }, [searchParams, setSearchParams, fetchSubscriptions, showToast]);

    const getSportLogo = (sport) => {
        const sportLogos = {
            'NBA': nbaLogo,
            'AFL': aflLogo,
        };
        return sportLogos[sport] || null;
    };

    /**
     * Filter subscriptions based on FREE_TIER logic:
     * - If user only has FREE_TIER, show it
     * - If user has paid subscriptions, hide FREE_TIER
     */
    const getDisplaySubscriptions = () => {
        const hasPaidSubscription = subscriptions.some(sub => sub.key !== 'FREE_TIER');
        
        if (hasPaidSubscription) {
            // User has paid subscriptions, filter out FREE_TIER
            return subscriptions.filter(sub => sub.key !== 'FREE_TIER');
        }
        
        // User only has FREE_TIER or no subscriptions, show all
        return subscriptions;
    };

    const displaySubscriptions = getDisplaySubscriptions();

    const getTotalMonthlyPrice = () => {
        return displaySubscriptions
            .filter(sub => sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due')
            .reduce((total, sub) => total + parseFloat(sub.monthly_price || 0), 0)
            .toFixed(2);
    };

    const getNextBillingDate = () => {
        const activeSubs = displaySubscriptions.filter(sub => 
            (sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due') && 
            sub.current_period_end
        );
        
        if (activeSubs.length === 0) return null;
        
        // Get the earliest billing date
        const dates = activeSubs.map(sub => new Date(sub.current_period_end));
        const earliestDate = new Date(Math.min(...dates));
        
        return subscriptionService.formatDate(earliestDate.toISOString());
    };

    // Check if user has subscribed to all available sports
    const hasAllSportsSubscribed = () => {
        if (availablePlans.length === 0 || subscriptions.length === 0) return false;

        // Get unique sports from available plans (excluding FREE_TIER sports if any)
        const availableSports = [...new Set(availablePlans.map(category => category.sport))];
        
        // Get sports user has active subscriptions for (including past_due since they still have the subscription)
        const subscribedSports = subscriptions
            .filter(sub => (sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due') && sub.key !== 'FREE_TIER')
            .map(sub => sub.sport);

        // Check if user has at least one active subscription for each available sport
        return availableSports.every(sport => subscribedSports.includes(sport));
    };

    const allSportsSubscribed = hasAllSportsSubscribed();

    // Get first paid subscription (active or cancelled) for portal actions
    const getFirstPaidSubscription = () => {
        return displaySubscriptions.find(sub => sub.key !== 'FREE_TIER');
    };
    
    // Get first active paid subscription for payment method updates
    // Includes past_due since they need to update payment to restore access
    const getFirstActiveSubscription = () => {
        return displaySubscriptions.find(sub => 
            sub.key !== 'FREE_TIER' && 
            (sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due')
        );
    };

    const handlePaymentMethodUpdate = async () => {
        const firstActiveSub = getFirstActiveSubscription();
        
        if (!firstActiveSub) {
            showToast('No paid subscriptions found', 'error');
            return;
        }

        setIsPaymentMethodLoading(true);

        // Don't pass plan_key - payment method is shared across all subscriptions for the customer
        const result = await subscriptionService.createPortalSession(
            null, 
            'payment_method_update'
        );

        if (result.success) {
            // Redirect to Stripe Customer Portal payment method update page
            window.location.href = result.data.url;
        } else {
            setIsPaymentMethodLoading(false);
            showToast(result.message || 'Failed to open payment method update. Please try again.', 'error');
        }
    };

    const handleBillingHistory = async () => {
        const firstPaidSub = getFirstPaidSubscription();
        
        if (!firstPaidSub) {
            showToast('No paid subscriptions found', 'error');
            return;
        }

        setIsBillingHistoryLoading(true);

        // Don't pass plan_key or flow_type - opens portal overview with ALL billing history
        const result = await subscriptionService.createPortalSession();

        if (result.success) {
            // Redirect to Stripe Customer Portal - shows all invoices and billing history
            window.location.href = result.data.url;
        } else {
            setIsBillingHistoryLoading(false);
            showToast(result.message || 'Failed to open billing history. Please try again.', 'error');
        }
    };

    const handleAdvanceTestClock = async (sport, subscriptionId) => {
        const days = parseInt(advanceDays[subscriptionId] || 0);
        
        if (days <= 0 || days > 30) {
            showToast('Please enter a value between 1 and 30 days', 'error');
            return;
        }

        setIsAdvancingClock(prev => ({ ...prev, [subscriptionId]: true }));

        const result = await devToolsService.advanceTestClock(sport, days);

        setIsAdvancingClock(prev => ({ ...prev, [subscriptionId]: false }));

        if (result.success) {
            // Store test clock info
            setTestClockInfo(prev => ({
                ...prev,
                [subscriptionId]: {
                    simulatedTime: result.simulatedTime,
                    realTime: result.realTime,
                    timeDifference: result.timeDifference
                }
            }));
            
            showToast(`✅ ${result.message}`, 'success');
            
            // Refresh subscriptions to show updated data
            setTimeout(() => {
                fetchSubscriptions();
            }, 2000);
        } else {
            showToast(result.message || 'Failed to advance test clock', 'error');
            if (result.hint) {
                setTimeout(() => {
                    showToast(result.hint, 'error');
                }, 2000);
            }
        }
    };

    const toggleDevTools = (subscriptionId) => {
        setDevToolsExpanded(prev => ({
            ...prev,
            [subscriptionId]: !prev[subscriptionId]
        }));
    };

    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
                    <p className="mt-4 text-slate-400">Loading subscriptions...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 p-4 sm:p-8">
            <div className="max-w-6xl mx-auto">
                <div className="mb-8">
                    <h2 className="text-3xl font-bold text-white mb-2">Manage Your Subscriptions</h2>
                    <p className="text-slate-400">View and manage all your active subscriptions</p>
                </div>

                {/* Success/Error Banner */}
                {successBanner && (
                    <div className={`mb-6 rounded-xl border p-4 flex items-start gap-3 animate-fade-in ${
                        successBanner.changeType === 'error'
                            ? 'bg-gradient-to-r from-red-500/10 via-orange-500/10 to-red-500/10 border-red-500/30'
                            : successBanner.changeType === 'upgrade'
                            ? 'bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-emerald-500/10 border-emerald-500/30'
                            : successBanner.changeType === 'downgrade'
                            ? 'bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 border-blue-500/30'
                            : 'bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-green-500/10 border-green-500/30'
                    }`}>
                        <div className="flex-shrink-0 mt-0.5">
                            {successBanner.changeType === 'error' ? (
                                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            ) : successBanner.changeType === 'upgrade' ? (
                                <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            ) : successBanner.changeType === 'downgrade' ? (
                                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            )}
                        </div>
                        <div className="flex-1">
                            <p className="text-sm text-slate-200 font-medium">
                                {successBanner.message}
                            </p>
                        </div>
                        <button
                            onClick={() => setSuccessBanner(null)}
                            className="flex-shrink-0 text-slate-400 hover:text-white transition-colors duration-200"
                            aria-label="Dismiss"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                )}

                {displaySubscriptions.length === 0 ? (
                    /* No Subscriptions State */
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl p-12 border border-slate-800/50 text-center">
                        <div className="w-20 h-20 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-10 h-10 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">No Active Subscriptions</h3>
                        <p className="text-slate-400 mb-6">You don't have any active subscriptions yet</p>
                        <button 
                            onClick={() => navigate('/')}
                            className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-3 rounded-lg font-semibold hover:from-emerald-700 hover:to-teal-700 transition duration-200 shadow-lg shadow-emerald-500/30"
                        >
                            Browse Plans
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Summary Card */}
                        <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl p-6 border border-slate-800/50 mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <p className="text-sm text-slate-400 mb-1">Active Subscriptions</p>
                                    <p className="text-3xl font-bold text-white">
                                        {displaySubscriptions.filter(s => s.status === 'active' || s.status === 'trialing' || s.status === 'past_due').length}
                                        {displaySubscriptions.some(s => s.status === 'past_due') && (
                                            <span className="text-xs bg-orange-500/10 text-orange-400 border border-orange-500/30 px-2 py-1 rounded-md ml-3 align-middle">
                                                Payment Issue
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-slate-400 mb-1">Total Monthly Cost</p>
                                    <p className="text-3xl font-bold text-emerald-400">${getTotalMonthlyPrice()}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-slate-400 mb-1">Next Billing Date</p>
                                    <p className="text-lg font-semibold text-white">{getNextBillingDate() || 'N/A'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Payment Issue Warning Banner - Show if any subscription is past_due */}
                        {subscriptions.some(sub => sub.status === 'past_due') && (
                            <div className="bg-gradient-to-r from-orange-500/10 via-red-500/10 to-orange-500/10 border border-orange-500/40 rounded-xl p-4 mb-6">
                                <p className="text-sm font-semibold text-orange-300 mb-1">
                                    <svg className="w-5 h-5 text-orange-400 inline-block align-middle mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <span className="align-middle">Action Required: Payment Issue</span>
                                </p>
                                <p className="text-sm text-slate-300 ml-7">
                                    One or more of your subscriptions has a payment issue and access has been suspended. Click <span className="font-semibold text-orange-400">Update Payment Method</span> on the affected subscription(s) below to restore access immediately.
                                </p>
                                <p className="text-xs text-orange-200 mt-2 ml-7">
                                    If payment is not received within 14 days, your subscription will be automatically cancelled.
                                </p>
                            </div>
                        )}

                        {/* Info Banner - Only show if user has paid subscriptions (including past_due) */}
                        {subscriptions.some(sub => sub.key !== 'FREE_TIER' && (sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due')) && (
                            <div className="bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
                                <div className="flex-shrink-0 mt-0.5">
                                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm text-slate-300">
                                        <span className="font-semibold text-white">Manage Your Subscriptions:</span> To upgrade, downgrade, or cancel your subscription for a specific sport, click the <span className="font-semibold text-emerald-400">Manage Subscription</span> button for that sport.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Subscriptions List */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                            {displaySubscriptions.map((subscription) => {
                                const statusInfo = subscriptionService.formatStatus(subscription.status);
                                const isActive = subscription.status === 'active' || subscription.status === 'trialing' || subscription.status === 'past_due';
                                
                                return (
                                    <div 
                                        key={subscription.user_subscription_id}
                                        className={`relative bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl p-6 border transition-all duration-300 group shadow-xl ${
                                            subscription.status === 'past_due' 
                                                ? 'border-orange-500/50 hover:border-orange-500/70 hover:shadow-2xl hover:shadow-orange-500/10'
                                                : 'border-slate-800/50 hover:border-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-500/10'
                                        }`}
                                    >
                                        {/* Decorative corner gradient */}
                                        <div className={`absolute top-0 right-0 w-32 h-32 rounded-bl-full blur-2xl ${
                                            subscription.status === 'past_due'
                                                ? 'bg-gradient-to-br from-orange-500/5 to-red-500/5'
                                                : 'bg-gradient-to-br from-emerald-500/5 to-teal-500/5'
                                        }`}></div>
                                        
                                        <div className="relative flex flex-col h-full">
                            {/* Header with Sport Icon */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition duration-300 p-2 flex-shrink-0 ${
                                        subscription.sport === 'NBA' || subscription.sport === 'AFL'
                                            ? 'bg-white' 
                                            : 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20'
                                    }`}>
                                        {getSportLogo(subscription.sport) ? (
                                            <img 
                                                src={getSportLogo(subscription.sport)} 
                                                alt={`${subscription.sport} Logo`}
                                                className="w-full h-full object-contain"
                                            />
                                        ) : (
                                            <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-white">
                                            {subscription.display_name || `${subscription.sport} ${subscription.tier}`}
                                        </h3>
                                    </div>
                                </div>

                                <span className={`${statusInfo.bgColor} ${statusInfo.borderColor} border px-3 py-1.5 rounded-lg text-xs font-semibold ${statusInfo.color} whitespace-nowrap`}>
                                    {subscription.status === 'trialing' && subscription.current_period_end ? (
                                        <>
                                            Trial ({Math.ceil((new Date(subscription.current_period_end) - new Date()) / 86400000)} days left)
                                        </>
                                    ) : (
                                        statusInfo.text
                                    )}
                                </span>
                            </div>

                                            {/* Access Status Banner for past_due */}
                                            {subscription.status === 'past_due' && (
                                                <div className="rounded-lg p-3 mb-4 border bg-orange-500/5 border-orange-500/20">
                                                    <div className="flex items-center gap-2">
                                                        <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                        </svg>
                                                        <p className="text-xs font-semibold text-orange-300">
                                                            Access Suspended
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Price Section */}
                                            <div className="bg-[#0a0f1e]/50 rounded-xl p-4 mb-4 border border-slate-800/30">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-xs text-slate-500 mb-1">Monthly Price</p>
                                                        <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                                                            ${parseFloat(subscription.monthly_price).toFixed(2)}
                                                        </p>
                                                    </div>
                                                    {isActive && (
                                                        <div className="text-right">
                                                            <p className="text-xs text-slate-500 mb-1">
                                                                {subscription.status === 'past_due' 
                                                                    ? 'Payment Due' 
                                                                    : subscription.canceled_at 
                                                                    ? 'Ends on' 
                                                                    : 'Renews on'
                                                                }
                                                            </p>
                                                            <p className={`text-sm font-semibold ${subscription.status === 'past_due' ? 'text-orange-400' : 'text-white'}`}>
                                                                {subscriptionService.formatDate(subscription.current_period_end).split(',')[0]}
                                                            </p>
                                                            <p className={`text-xs ${subscription.status === 'past_due' ? 'text-orange-400/70' : 'text-slate-500'}`}>
                                                                {subscriptionService.formatDate(subscription.current_period_end).split(',')[1]}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Scheduled Change Info */}
                                            {subscription.scheduled_change && (
                                                <div className="bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 border border-blue-500/30 rounded-xl p-3 mb-4">
                                                    <div className="flex items-start gap-2.5">
                                                        <div className="flex-shrink-0 mt-0.5">
                                                            <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="text-xs font-semibold text-blue-300 mb-1">Scheduled Change</p>
                                                            <p className="text-xs text-slate-300">
                                                                Downgrading to <span className="font-semibold text-white">{subscription.scheduled_change.to_display_name}</span> on {subscriptionService.formatDate(subscription.scheduled_change.effective_date).split(',')[0]}
                                                            </p>
                                                            <p className="text-xs text-slate-400 mt-1.5">
                                                                If you change your mind and want to cancel the change, click on <span className="font-semibold text-slate-300">Manage Subscription</span>.
                                                            </p>
                                                            <p className="text-xs text-slate-400 mt-1">
                                                                New price: <span className="font-semibold text-emerald-400">${parseFloat(subscription.scheduled_change.to_monthly_price).toFixed(2)}/mo</span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Features/Info */}
                                            <div className="space-y-2 mb-4 flex-grow">
                                                <div className="flex items-center text-sm text-slate-400">
                                                    <svg className="w-4 h-4 text-emerald-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {subscription.key === 'FREE_TIER' 
                                                        ? 'Base tier - this will always be free.' 
                                                        : 'Managed via Stripe'
                                                    }
                                                </div>

                                                {/* Payment Failed - Past Due Status */}
                                                {subscription.status === 'past_due' && (
                                                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-sm">
                                                        <p className="text-orange-400 font-semibold mb-1">
                                                            <svg className="w-4 h-4 text-orange-400 inline-block align-middle mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                            </svg>
                                                            <span className="align-middle">Payment Failed</span>
                                                        </p>
                                                        <p className="text-orange-300 mb-2 ml-6">
                                                            Your payment could not be processed. Access has been temporarily suspended. Stripe will automatically retry payment over the next 14 days.
                                                        </p>
                                                        <p className="text-orange-200 text-xs mb-1.5 ml-6">
                                                            Update your payment method now to restore access immediately.
                                                        </p>
                                                        <p className="text-orange-300/70 text-xs ml-6">
                                                            If payment is not received within 14 days, your subscription will be automatically cancelled.
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Canceled Subscription */}
                                                {subscription.canceled_at && (
                                                    <>
                                                        {subscription.cancellation_reason === 'dispute' ? (
                                                            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm">
                                                                <div className="flex items-start gap-2">
                                                                    <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                    </svg>
                                                                    <div className="flex-1">
                                                                        <p className="text-red-400 font-semibold mb-1">Subscription Cancelled - Payment Dispute</p>
                                                                        <p className="text-red-300">
                                                                            Your subscription was cancelled due to a dispute on the payment. If you think this is an error please contact support.
                                                                        </p>
                                                                        {subscription.dispute_id && (
                                                                            <p className="text-xs text-red-400/80 mt-2">
                                                                                Dispute ID: {subscription.dispute_id}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center text-sm text-red-400">
                                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                </svg>
                                                                Subscription will end on {subscriptionService.formatDate(subscription.current_period_end)}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>

                            {/* Action Button */}
                            <button 
                                onClick={() => {
                                    if (subscription.key !== 'FREE_TIER' && subscription.cancellation_reason !== 'dispute') {
                                        // For past_due, open portal directly to payment method update
                                        if (subscription.status === 'past_due') {
                                            handlePaymentMethodUpdate();
                                        } else {
                                            setSelectedSubscription(subscription);
                                            setIsManageModalOpen(true);
                                        }
                                    }
                                }}
                                disabled={subscription.key === 'FREE_TIER' || subscription.cancellation_reason === 'dispute'}
                                className={`w-full font-semibold py-3 rounded-lg transition duration-200 flex items-center justify-center gap-2 mt-auto ${
                                    subscription.key === 'FREE_TIER' || subscription.cancellation_reason === 'dispute'
                                        ? 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
                                        : subscription.status === 'past_due'
                                        ? 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30 hover:scale-[1.02] transform'
                                        : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30 hover:scale-[1.02] transform'
                                }`}
                            >
                                {subscription.status === 'past_due' ? (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                        </svg>
                                        Update Payment Method
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        {subscription.cancellation_reason === 'dispute' ? 'Contact Support' : 'Manage Subscription'}
                                    </>
                                )}
                            </button>

                            {/* Dev Tools - Test Clock Advancement */}
                            {isDevMode && subscription.key !== 'FREE_TIER' && (
                                <div className="mt-4 border-t border-slate-700/50 pt-4">
                                    <button
                                        onClick={() => toggleDevTools(subscription.user_subscription_id)}
                                        className="w-full flex items-center justify-between text-xs text-slate-400 hover:text-slate-300 transition-colors duration-200"
                                    >
                                        <span className="flex items-center gap-2">
                                            <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                            </svg>
                                            Dev Tools: Test Clock
                                        </span>
                                        <svg 
                                            className={`w-4 h-4 transition-transform duration-200 ${devToolsExpanded[subscription.user_subscription_id] ? 'rotate-180' : ''}`} 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {devToolsExpanded[subscription.user_subscription_id] && (
                                        <div className="mt-3 bg-purple-500/5 border border-purple-500/20 rounded-lg p-3">
                                            <p className="text-xs text-purple-300 mb-3 font-medium">
                                                Advance subscription time for testing
                                            </p>

                                            {/* Test Clock Info */}
                                            {testClockInfo[subscription.user_subscription_id] && (
                                                <div className="mb-3 p-2 bg-purple-500/10 border border-purple-500/30 rounded text-xs space-y-1">
                                                    <p className="text-purple-200">
                                                        <span className="font-semibold">Simulated Time:</span>{' '}
                                                        {new Date(testClockInfo[subscription.user_subscription_id].simulatedTime).toLocaleString()}
                                                    </p>
                                                    <p className="text-purple-300/70">
                                                        {testClockInfo[subscription.user_subscription_id].timeDifference}
                                                    </p>
                                                </div>
                                            )}

                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="30"
                                                    value={advanceDays[subscription.user_subscription_id] || ''}
                                                    onChange={(e) => setAdvanceDays(prev => ({
                                                        ...prev,
                                                        [subscription.user_subscription_id]: e.target.value
                                                    }))}
                                                    placeholder="Days"
                                                    className="flex-1 px-3 py-2 bg-slate-800/50 border border-slate-700 rounded text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                                                    disabled={isAdvancingClock[subscription.user_subscription_id]}
                                                />
                                                <button
                                                    onClick={() => handleAdvanceTestClock(subscription.sport, subscription.user_subscription_id)}
                                                    disabled={isAdvancingClock[subscription.user_subscription_id]}
                                                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                >
                                                    {isAdvancingClock[subscription.user_subscription_id] ? (
                                                        <>
                                                            <div className="inline-block animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                                                            <span>Advancing...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            <span>Advance</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                            <p className="text-xs text-slate-500 mt-2">
                                                Enter 1-30 days to advance the subscription's test clock
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Add New Subscription Card - Only show if not subscribed to all sports */}
                            {!allSportsSubscribed && (
                                <button
                                    onClick={() => setIsPlansModalOpen(true)}
                                    className="relative bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl p-6 border border-slate-800/50 hover:border-emerald-500/30 transition-all duration-300 group shadow-xl hover:shadow-2xl hover:shadow-emerald-500/10 text-left"
                                >
                                    {/* Decorative corner gradient */}
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-500/5 to-teal-500/5 rounded-bl-full blur-2xl"></div>
                                    
                                    <div className="relative flex flex-col items-center justify-center min-h-[300px]">
                                        {/* Plus Icon */}
                                        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition duration-300">
                                            <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                            </svg>
                                        </div>
                                        
                                        <h3 className="text-xl font-bold text-white mb-2">Add New Subscription</h3>
                                        <p className="text-sm text-slate-400 text-center">Browse available plans and upgrade your experience</p>
                                    </div>
                                </button>
                            )}
                        </div>

                        {/* Additional Actions */}
                        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <button 
                                onClick={handlePaymentMethodUpdate}
                                disabled={isPaymentMethodLoading || !getFirstActiveSubscription()}
                                className="bg-slate-800/30 hover:bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-left transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                                            {isPaymentMethodLoading ? (
                                                <>
                                                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                                    <span>Loading...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                    </svg>
                                                    Payment Method
                                                </>
                                            )}
                                        </h4>
                                        <p className="text-sm text-slate-400">You will be redirected to Stripe for secure processing</p>
                                    </div>
                                    <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </button>
                            <button 
                                onClick={handleBillingHistory}
                                disabled={isBillingHistoryLoading || !getFirstPaidSubscription()}
                                className="bg-slate-800/30 hover:bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-left transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                                            {isBillingHistoryLoading ? (
                                                <>
                                                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                                    <span>Loading...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    Billing History
                                                </>
                                            )}
                                        </h4>
                                        <p className="text-sm text-slate-400">You will be redirected to Stripe for secure processing</p>
                                    </div>
                                    <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Toast Notification */}
            {toast && (
                <Toast 
                    message={toast.message}
                    type={toast.type}
                    onClose={closeToast}
                />
            )}

            {/* Subscription Plans Modal */}
            <SubscriptionPlansModal 
                isOpen={isPlansModalOpen}
                onClose={() => setIsPlansModalOpen(false)}
            />

            {/* Manage Existing Subscription Modal */}
            <ManageExistingSportSubscription 
                isOpen={isManageModalOpen}
                onClose={() => {
                    setIsManageModalOpen(false);
                    setSelectedSubscription(null);
                }}
                subscription={selectedSubscription}
                onSubscriptionChanged={(message, changeType) => {
                    // Show success banner
                    setSuccessBanner({ message, changeType });
                    // Refresh subscriptions
                    fetchSubscriptions();
                }}
            />
        </div>
    );
}

export default ManageSubscriptions;
